import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Swipeable media carousel used inside feed posts.
 * - Combines video + images into one unified array
 * - Container height follows each item's natural aspect ratio (no forced height)
 * - Preloads adjacent images to eliminate swipe delay
 * - Dot indicators + slide counter for multi-item posts
 * - Calls onOpenItem(index) when a photo is tapped (to open fullscreen)
 */
const PostCarousel = ({ images = [], video = null, onOpenItem }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // naturalRatio[i] = naturalHeight / naturalWidth for image i
  const [naturalRatio, setNaturalRatio] = useState({});
  const touchStartX = useRef(null);
  const touchStartTime = useRef(null);

  // Video first, then images
  const media = [
    ...(video?.url ? [{ type: 'video', url: video.url, thumbnail: video.thumbnail }] : []),
    ...(images || []).map(url => ({ type: 'image', url })),
  ];

  // Record natural ratio when an image loads (used for container sizing)
  const recordRatio = useCallback((idx, w, h) => {
    if (w && h) {
      setNaturalRatio(prev => prev[idx] !== undefined ? prev : { ...prev, [idx]: h / w });
    }
  }, []);

  // Preload neighbors whenever index changes
  useEffect(() => {
    [-1, 1].forEach(offset => {
      const idx = currentIndex + offset;
      if (idx < 0 || idx >= media.length) return;
      if (media[idx].type !== 'image') return;
      const img = new window.Image();
      img.onload = () => recordRatio(idx, img.naturalWidth, img.naturalHeight);
      img.src = media[idx].url;
    });
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (media.length === 0) return null;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    const elapsed = Date.now() - touchStartTime.current;
    if (Math.abs(diff) > 50 && elapsed < 350) {
      if (diff > 0 && currentIndex < media.length - 1) setCurrentIndex(i => i + 1);
      else if (diff < 0 && currentIndex > 0) setCurrentIndex(i => i - 1);
    }
    touchStartX.current = null;
    touchStartTime.current = null;
  };

  const item = media[currentIndex];

  // Cap portrait images at 4:5 (ratio 1.25) so they don't fill the whole screen
  const ratio = naturalRatio[currentIndex];
  const clampedRatio = ratio ? Math.min(ratio, 1.25) : null;

  // Container style: use aspect-ratio once we know the image dimensions;
  // before that, `h-auto` on the img drives the height naturally.
  const containerStyle = clampedRatio ? { aspectRatio: `1 / ${clampedRatio}` } : {};

  return (
    <div
      className="relative w-full bg-black overflow-hidden select-none"
      style={containerStyle}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {item.type === 'video' ? (
        <video
          src={item.url}
          poster={item.thumbnail}
          className="w-full h-full object-contain"
          controls
          loop
          playsInline
        />
      ) : (
        <img
          src={item.url}
          alt=""
          className={clampedRatio ? 'w-full h-full object-contain cursor-pointer' : 'w-full h-auto block cursor-pointer'}
          draggable={false}
          onLoad={e => recordRatio(currentIndex, e.target.naturalWidth, e.target.naturalHeight)}
          onClick={() => onOpenItem?.(currentIndex)}
        />
      )}

      {/* Hidden preload images for neighbours (also captures their ratios) */}
      {[-1, 1].map(offset => {
        const idx = currentIndex + offset;
        if (idx < 0 || idx >= media.length || media[idx].type !== 'image') return null;
        return (
          <img
            key={idx}
            src={media[idx].url}
            alt=""
            aria-hidden="true"
            className="hidden"
            onLoad={e => recordRatio(idx, e.target.naturalWidth, e.target.naturalHeight)}
          />
        );
      })}

      {/* Dot indicators */}
      {media.length > 1 && (
        <>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center space-x-1 pointer-events-none">
            {media.map((_, i) => (
              <div
                key={i}
                className={`rounded-full transition-all duration-200 ${
                  i === currentIndex ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
          {/* Slide counter badge */}
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-medium px-2 py-0.5 rounded-full pointer-events-none">
            {currentIndex + 1}/{media.length}
          </div>
        </>
      )}
    </div>
  );
};

export default PostCarousel;
